import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";
import { produce } from "immer";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineActions } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";

import { assertNonNullable } from "../../../utilities/assert";
import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { handleIfOutdated, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { defaultLocalizer, localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { extractCause, isRawHttpStatusCause, translateEldoradoCause } from "../../../utilities/error";
import { createCustomNotificationAction } from "../../../app/actions";
import { validateLockState } from "../../../utilities/lockProcessUtils";
import { lockProcessError } from "../../processFormEngine/store/actions";

import { isArchiveModalDocumentData } from "../archiveModalTypes";
import { EldoradoSendArchivePayload, sendArchiveToEldorado } from "../actions";
import { ArchiveModuleFormDescriptor } from "../descriptors";

import { submitXakteArchive } from "./xakteArchiveSaga";

const logger = LoggerFactory.getLogger("sendEldoradoArchiveSaga");

export function* sendEldoradoArchiveSaga(): SagaGenerator<void> {
	yield* takeLatest(sendArchiveToEldorado.type, function* (action: Action<EldoradoSendArchivePayload>) {
		if (yield* call(handleIfOutdated)) {
			return;
		}

		const lockValidationResult = yield* call(validateLockState);
		if (!lockValidationResult) {
			yield* put(lockProcessError({}));
			return;
		}

		const { jobUuid, activityId } = action.payload;
		const activity: Activity | undefined = yield* select(ActivitySelectors.activityById(activityId));
		assertNonNullable(activity);
		if (
			ArchiveModuleFormDescriptor.isInstance(activity.descriptor) &&
			activity.descriptor.archiveSystem === "xakte"
		) {
			yield* call(submitXakteArchive, activityId, activity.descriptor.sourceActivityId, jobUuid);
			return;
		}

		const startData = yield* select(ActivitySelectors.data(activityId));
		if (isArchiveModalDocumentData(startData)) {
			yield* put(
				ActivityActions.setData({
					activityId,
					data: produce(startData, draft => {
						if (!draft.document.root.metadata) {
							draft.document.root.metadata = {};
						}
						draft.document.root.metadata.busy = true;
					})
				})
			);
		}

		yield* call(
			fetchSagaFactory(
				{
					relativeUrl: `/eldorado/completeArchiving?conversionId=${jobUuid}`,
					method: "POST"
				},
				function* handleResponse(_response: Response) {
					// TODO validate path to archive group here
					const documentActivity = yield* select(
						ActivitySelectors.activityById(activity.descriptor.documentActivityId!)
					);
					if (documentActivity) {
						yield* call(reloadTaskAndAntragActivity);
					}

					const successData = yield* select(ActivitySelectors.data(activityId));
					if (isArchiveModalDocumentData(successData)) {
						yield* put(
							ActivityActions.setData({
								activityId,
								data: produce(successData, draft => {
									if (!draft.document.root.metadata) {
										draft.document.root.metadata = {};
									}
									draft.document.root.metadata.busy = false;
								})
							})
						);
					}
					yield* put(ActivityActions.cancel({ activityId }));
				},
				function* handleError(error: unknown) {
					logger.error("completeArchiving failed:", error);
					const rawCause = extractCause(error);
					const cause = rawCause !== undefined ? translateEldoradoCause(rawCause) : undefined;
					// Don't show raw HTTP status lines (e.g. "500: Internal Server Error") as cause
					const localizableMessage =
						cause !== undefined && !isRawHttpStatusCause(cause)
							? localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.transmittingErrorWithCause,
									{ cause: { type: "plain", value: cause } }
								)
							: localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.transmittingError
								);

					// Reset preview URL and jobUuid so the modal returns to an editable/generatable state
					const currentData = yield* select(ActivitySelectors.data(activityId));
					if (isArchiveModalDocumentData(currentData)) {
						const errorMessage = (defaultLocalizer(localizableMessage) ?? "").trim();
						const resetDocument = produce(currentData, draft => {
							if (!draft.document.root.metadata) {
								draft.document.root.metadata = {};
							}
							draft.document.root.metadata.status = "error";
							draft.document.root.metadata.message = errorMessage;
							draft.document.root.metadata.busy = false;
							draft.document.root.metadata.cancelDisabled = false;
							delete draft.document.root.metadata["archivePreviewUrl"];
							delete draft.document.root.metadata["jobUuid"];
						});
						yield* put(ActivityActions.setData({ activityId, data: resetDocument }));
					}
					yield* put(FormEngineActions.command({ activityId, engineEvent: Commands.setReadonly(false) }));
					yield* put(
						createCustomNotificationAction({
							title: { key: RESOURCE_KEYS.features.notification.title.error },
							message: localizableMessage,
							severity: "error",
							activityId,
							group: `eldoradoArchiving:${activityId}`
						})
					);
				}
			)
		);
	});
}
